import { UseMutationResult, UseQueryOptions, UseQueryResult, useMutation, useQuery, useQueryClient } from 'react-query'

import { useDispatch } from 'react-redux'

import { notifications, user } from '@percept/redux/bundles'

import { broadcastSignOut } from '@percept/constants'

import { apiClients, makeClientQueryFn } from './lib'

import { AxiosError } from 'axios'

import qs from 'query-string'

import { saveAs } from 'file-saver'

import { filterTruthyMapping } from '@percept/utils'

import {
  CreateTenantUserPayload,
  EditTenantUserPayload,
  PaginatedResponse,
  StateOnlyResponse,
  TenantUserManagementMetadata,
  UserStatus,
} from '@percept/types'


export const useSignOut = (): UseMutationResult<StateOnlyResponse, undefined, void, void> => {
  const dispatch = useDispatch()
  const notificationId = 'sign-out'
  const notificationActionCreator = notifications.actions.addNotification
  return useMutation({
    onMutate: () => {
      dispatch(notificationActionCreator({
        id: notificationId,
        ttl: 0,
        type: 'info',
        loading: true,
        name: 'Sign Out',
        message: 'Signing you out...',
      }))
    },
    onError: () => {
      dispatch(notificationActionCreator({
        id: notificationId,
        ttl: 0,
        type: 'error',
        loading: false,
        name: 'Sign Out',
        message: 'Sign out failed',
      }))
    },
    onSuccess: () => {
      dispatch(notificationActionCreator({
        id: notificationId,
        type: 'success',
        loading: false,
        name: 'Sign Out',
        message: 'You have been succesfully signed out',
      }))
      broadcastSignOut()
      dispatch(user.actions.signOut())
    },
    mutationFn: async () => {
      return await apiClients.core.post('/users/me/sign-out')
    },
  })
}


export const useTenantUserManagementMetadata = (
  tenant_id: string
): UseQueryResult<TenantUserManagementMetadata> => (
  useQuery({
    queryKey: ['userManagementMetadata', tenant_id],
    queryFn: makeClientQueryFn<TenantUserManagementMetadata>(
      apiClients.core,
      { url: `/tenant-management/${tenant_id}/users/metadata` },
      (response) => ({
        ...response.data,
        attribute_groups: response.data.attribute_groups.map( g => ({
          ...g,
          values: g.values.map( v => ({
            ...v,
            blueprint: v.slug === 'vodafone-user',
          }))
        }))
      })
    ),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })
)


export type ListingUserParameter = {
  id: string
  slug: string
  name: string
  enabled: boolean
}

export type TenantUserManagementListingUser = {
  user_id: string
  cognito_id: string
  cognito_status: UserStatus | null
  enabled: boolean
  default_org_unit_id: string
  tenant_org_unit_id: string
  email: string
  name?: string
  created: string
  activated_at: string
  modified: string
  last_login?: string
  user_attributes: {
    group: ListingUserParameter
    value: ListingUserParameter
  }[]
  user_options: {
    group: ListingUserParameter
    values: ListingUserParameter[]
  }[]
}


export const useTenantUserManagementEstablishedUsers = (
  tenant_id: string,
  page: number,
  queryOptions: Partial<UseQueryOptions<PaginatedResponse<TenantUserManagementListingUser[]>>> = {}
): UseQueryResult<PaginatedResponse<TenantUserManagementListingUser[]>> => (
  useQuery({
    queryKey: ['userManagementEstablishedUsers', tenant_id, page],
    queryFn: makeClientQueryFn<PaginatedResponse<TenantUserManagementListingUser[]>>(apiClients.core, {
      url: `/tenant-management/${tenant_id}/users/established-users`,
      params: { page }
    }),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
    ...queryOptions,
  })
)

export type SearchMatchType = 'EXACT' | 'STARTS_WITH'

export type TenantUserFilterProps = {
  tenant_id: string
  query?: string | null
  marketSlugs?: string[] | null
  userTypeSlugs?: string[] | null
  searchField?: 'status' | 'email' | null
  matchType?: SearchMatchType | null
}

export const useSearchTenantUserManagementEstablishedUsers = ({
  tenant_id,
  searchField,
  query,
  matchType = 'EXACT',
  marketSlugs,
  userTypeSlugs,
  ...queryOptions
}: TenantUserFilterProps & Partial<UseQueryOptions<TenantUserManagementListingUser[]>>
): UseQueryResult<TenantUserManagementListingUser[]> => (
  useQuery({
    queryKey: [
      'userManagementEstablishedUsersSearch',
      tenant_id,
      searchField,
      query,
      matchType,
      marketSlugs && marketSlugs.sort(),
      userTypeSlugs && userTypeSlugs.sort(),
    ],
    queryFn: makeClientQueryFn<TenantUserManagementListingUser[]>(apiClients.core, {
      url: `/tenant-management/${tenant_id}/users/search-established-users`,
      params: filterTruthyMapping({
        search_field: searchField,
        search_query: query,
        search_match_type: matchType && matchType.toLowerCase(),
        market_slugs: marketSlugs,
        user_type_slugs: userTypeSlugs,
      }),
      paramsSerializer: qs.stringify,
    }),
    refetchOnWindowFocus: false,
    retry: false,
    ...queryOptions,
  })
)


export const useTenantUsersCSVExport = (): UseMutationResult<
  void, unknown, TenantUserFilterProps
> => {
  return useMutation({
    mutationFn: async ({
      tenant_id,
      query,
      matchType = 'EXACT',
      searchField,
      marketSlugs,
      userTypeSlugs,
    }: TenantUserFilterProps) => {
      const response = await apiClients.core.get<Blob>(
        `/tenant-management/${tenant_id}/users/export-established-users`,
        {
          params: filterTruthyMapping({
            search_field: searchField,
            search_query: query,
            search_match_type: matchType && matchType.toLowerCase(),
            market_slugs: marketSlugs,
            user_type_slugs: userTypeSlugs,
          }),
          paramsSerializer: qs.stringify,
          responseType: 'blob',
          headers: {
            'Content-Type': 'text/csv',
          }
        }
      )

      // Get filename from content disposition header if available
      const contentDisposition: string = response.headers && response.headers['content-disposition']
      // Set default filename if not found in response headers
      let filename = 'users.csv'
      if( contentDisposition ){
        const headerValues = contentDisposition.split(';').map( s => s.trim())
        const filenameValue = headerValues.find( v => v.startsWith('filename='))
        if( filenameValue ){
          filename = filenameValue.replace('filename=', '')
        }
      }
      saveAs(response.data, filename)
    }
  })
}


export const useTenantUserManagementEstablishedUser = (
  tenant_id: string,
  user_id: string | null,
  queryOptions: Partial<UseQueryOptions<TenantUserManagementListingUser>> = {}
): UseQueryResult<TenantUserManagementListingUser> => (
  useQuery({
    queryKey: ['userManagementEstablishedUsers', tenant_id, user_id],
    queryFn: makeClientQueryFn<TenantUserManagementListingUser>(apiClients.core, {
      url: `/tenant-management/${tenant_id}/users/established-users/${user_id}`,
    }),
    refetchOnWindowFocus: false,
    retry: false,
    ...queryOptions,
  })
)


export const useTenantUserManagementPendingSSOUsers = ({
  tenant_id,
  page,
  emailFilter,
  searchMatchType,
  marketSlugs,
  ...queryOptions
}: {
  tenant_id: string
  page: number
  emailFilter?: string | null
  searchMatchType?: SearchMatchType | null
  marketSlugs?: string[] | null
} & Partial<UseQueryOptions<PaginatedResponse<TenantUserManagementListingUser[]>>>
): UseQueryResult<PaginatedResponse<TenantUserManagementListingUser[]>> => (
  useQuery({
    queryKey: ['userManagementPendingSSOUsers', tenant_id, emailFilter, searchMatchType, marketSlugs && marketSlugs.sort(), page],
    queryFn: makeClientQueryFn<PaginatedResponse<TenantUserManagementListingUser[]>>(apiClients.core, {
      url: `/tenant-management/${tenant_id}/users/pending-sso-users`,
      params: {
        page,
        ...filterTruthyMapping({
          search_match_type: searchMatchType && searchMatchType.toLowerCase(),
          search_query: emailFilter,
          market_slugs: marketSlugs,
        })
      },
      paramsSerializer: qs.stringify,
    }),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
    ...queryOptions,
  })
)


export const useTenantUser = (
  tenant_id: string,
  user_id: string | null,
  queryOptions: Partial<UseQueryOptions<TenantUserManagementListingUser>> = {}
): UseQueryResult<TenantUserManagementListingUser> => (
  useQuery({
    queryKey: ['tenantUser', tenant_id, user_id],
    queryFn: makeClientQueryFn<TenantUserManagementListingUser>(
      apiClients.core,
      { url: `/tenant-management/${tenant_id}/users/established-users/${user_id}` },
    ),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!user_id,
    retry: false,
    ...queryOptions,
  })
)


type CreateUserResponse = {
  user_id: string
  enabled: true
  default_org_unit_id: string
  tenant_org_unit_id: string
  name: string
  email: string
  previous_login: string | null
}

type CreateUserError = {
  detail: {
    cognito_id: string | null
    user_id: string
  }
}


export const useTenantUserManagementCreateUser = (
  tenant_id: string
): UseMutationResult<CreateUserResponse, AxiosError<CreateUserError>, CreateTenantUserPayload> => {
  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  return useMutation({
    mutationKey: ['userManagementCreateUser', tenant_id],
    mutationFn: async (payload: CreateTenantUserPayload) => {
      const response = await apiClients.core.post<CreateUserResponse>(
        `/tenant-management/${tenant_id}/users/established-users`,
        payload
      )
      return response.data
    },
    onMutate: (payload): void => {
      dispatch(
        notifications.actions.addNotification({
          id: 'create-user',
          type: 'info',
          name: 'Creating User',
          loading: true,
          ttl: 0,
          message: `Creating user ${payload.email}...`,
        })
      )
    },
    onError: (error, payload): void => {
      const errorMessage = (
        error.response && error.response.status === 409 ?
          'user already exists' :
          error.message
      )
      dispatch(
        notifications.actions.addNotification({
          id: 'create-user',
          type: 'error',
          name: 'User Creation Failed',
          ttl: 5000,
          loading: false,
          message: `User ${payload.email} could not be created - ${errorMessage}`,
        })
      )
    },
    onSuccess: (response): void => {
      queryClient.invalidateQueries(['userManagementEstablishedUsers', tenant_id])
      dispatch(
        notifications.actions.addNotification({
          id: 'create-user',
          type: 'success',
          name: 'User Created',
          loading: false,
          message: `User ${response.email} was successfully created`,
        })
      )
    }
  })
}


type CreateSSOUserResponse = Omit<CreateUserResponse, 'name' | 'previous_login'>


export const useTenantUserManagementCreateSSOUser = (
  tenant_id: string
): UseMutationResult<CreateSSOUserResponse, AxiosError<CreateUserError>, CreateTenantUserPayload> => {
  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  return useMutation({
    mutationKey: ['userManagementCreateSSOUser', tenant_id],
    mutationFn: async (payload: CreateTenantUserPayload) => {
      const response = await apiClients.core.post<CreateSSOUserResponse>(
        `/tenant-management/${tenant_id}/users/pending-sso-users`,
        payload
      )
      return response.data
    },
    onMutate: (payload): void => {
      dispatch(
        notifications.actions.addNotification({
          id: 'create-user',
          type: 'info',
          name: 'Creating User',
          loading: true,
          ttl: 0,
          message: `Creating user ${payload.email}...`,
        })
      )
    },
    onError: (error, payload): void => {
      const errorMessage = (
        error.response && error.response.status === 409 ?
          'user already exists' :
          error.message
      )
      dispatch(
        notifications.actions.addNotification({
          id: 'create-user',
          type: 'error',
          name: 'User Creation Failed',
          ttl: 5000,
          loading: false,
          message: `User ${payload.email} could not be created - ${errorMessage}`,
        })
      )
    },
    onSuccess: (response): void => {
      queryClient.invalidateQueries(['userManagementPendingSSOUsers', tenant_id])
      dispatch(
        notifications.actions.addNotification({
          id: 'create-user',
          type: 'success',
          name: 'User Created',
          loading: false,
          message: `User ${response.email} was successfully created`,
        })
      )
    },
  })
}


export const useEditTenantUser = (
  tenant_id: string
): UseMutationResult<CreateSSOUserResponse, AxiosError, EditTenantUserPayload> => {
  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  return useMutation({
    mutationKey: ['userManagementEditTenantUser', tenant_id],
    mutationFn: async ({ user_id, ...payload }: EditTenantUserPayload) => {
      const response = await apiClients.core.put<CreateSSOUserResponse>(
        `/tenant-management/${tenant_id}/users/established-users/${user_id}`,
        payload
      )
      return response.data
    },
    onError: (error, payload): void => {
      dispatch(
        notifications.actions.addNotification({
          type: 'error',
          name: 'User Edit Failed',
          ttl: 0,
          message: `User ${payload.email} could not be edited - ${error.message}`,
        })
      )
    },
    onSuccess: (response): void => {
      queryClient.invalidateQueries(['userManagementEstablishedUsers', tenant_id])
      queryClient.invalidateQueries(['userManagementEstablishedUsersSearch', tenant_id])
      queryClient.invalidateQueries(['tenantUser', tenant_id, response.user_id])
      dispatch(
        notifications.actions.addNotification({
          type: 'success',
          name: 'User Edited',
          message: `User ${response.email} was successfully edited`,
        })
      )
    }
  })
}


export const useEditPendingSSOUser = (
  tenant_id: string
): UseMutationResult<CreateSSOUserResponse, AxiosError, EditTenantUserPayload> => {
  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  return useMutation({
    mutationKey: ['userManagementEditPendingSSOUser', tenant_id],
    mutationFn: async ({ user_id, ...payload }: EditTenantUserPayload) => {
      const response = await apiClients.core.put<CreateSSOUserResponse>(
        `/tenant-management/${tenant_id}/users/pending-sso-users/${user_id}`,
        payload
      )
      return response.data
    },
    onError: (error, payload): void => {
      dispatch(
        notifications.actions.addNotification({
          type: 'error',
          name: 'User Edit Failed',
          ttl: 0,
          message: `User ${payload.email} could not be edited - ${error.message}`,
        })
      )
    },
    onSuccess: (response): void => {
      queryClient.invalidateQueries(['userManagementPendingSSOUsers', tenant_id])
      queryClient.invalidateQueries(['tenantUser', tenant_id, response.user_id])
      dispatch(
        notifications.actions.addNotification({
          type: 'success',
          name: 'User Edited',
          message: `User ${response.email} was successfully edited`,
        })
      )
    }
  })
}


type UserInviteParams = {
  cognito_id: string
  email: string
}

export const useResendUserInvitation = (): UseMutationResult<unknown, AxiosError, UserInviteParams> => {
  const dispatch = useDispatch()
  return useMutation<unknown, AxiosError, UserInviteParams>({
    mutationKey: ['resendUserInvitation'],
    mutationFn: async ({ cognito_id }: UserInviteParams) => {
      const response = await apiClients.core.post(
        '/users/resend-invitation',
        { cognito_id }
      )
      return response.data
    },
    onMutate: (payload): void => {
      dispatch(
        notifications.actions.addNotification({
          id: 'resend-user-invite',
          type: 'info',
          loading: true,
          name: 'Resending Invitation',
          message: `Resending invitation for user ${payload.email}...`,
          ttl: null,
        })
      )
    },
    onError: (error, payload): void => {
      dispatch(
        notifications.actions.addNotification({
          id: 'resend-user-invite',
          type: 'error',
          name: 'Resend User Invitation Failed',
          ttl: 0,
          message: `Invitation could not be resent to user ${payload.email} - ${error.message}`,
        })
      )
    },
    onSuccess: (response, payload): void => {
      dispatch(
        notifications.actions.addNotification({
          id: 'resend-user-invite',
          type: 'success',
          name: 'Invitation Resent',
          message: `Invitation successfully resent to user ${payload.email}`,
        })
      )
    }
  })
}
